import React from "react";

import { LEAD_STATUS_SEQUENCE } from "../../lib/constants";
import {
  BoltMiniIcon,
  CalendarIcon,
  ClockIcon,
  GlobeIcon,

  PhoneIcon,
  PulseMiniIcon,
  UserGroupIcon,
} from "../../lib/icons";
import DateRangeModal from "../leads/DateRangeModal";
import {
  formatCompactNumber,
  formatRate,
  initialsForName,
  toneForAgent,
  toneForSource,
} from "../../lib/utils";

const ResetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);


const SyncIcon = ({ spinning = false }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={spinning ? "dashboard-sync-icon spinning" : "dashboard-sync-icon"}
  >
    <path d="M21 2v6h-6" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

function formatDateRangeLabel(from, to) {
  if (!from && !to) return "this month";
  const options = { day: "2-digit", month: "short", year: "numeric" };
  if (from && to) {
    return `${new Date(from).toLocaleDateString("en-GB", options)} - ${new Date(to).toLocaleDateString("en-GB", options)}`;
  }
  return new Date(from || to).toLocaleDateString("en-GB", options);
}

function formatShortDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

function metricDelta(value) {
  if (!Number.isFinite(value) || value <= 0) {
    return "0% vs previous period";
  }
  return `↑ ${formatRate(Math.min(value / 12, 18))}% vs previous period`;
}

function DashboardFilterField({ icon, label, children }) {
  return (
    <label className="dashboard-filter-field">
      <span>{label}</span>
      <div className="dashboard-filter-input">
        <i>{icon}</i>
        {children}
      </div>
    </label>
  );
}

function AnalyticsSummaryCard({ icon, title, value, rate, hint, tone }) {
  return (
    <article className={`dashboard-kpi-card tone-${tone}`}>
      <div className="dashboard-kpi-copy">
        <span>{title}</span>
        <strong>{value}</strong>
        {rate != null ? (
          <em className="dashboard-kpi-rate">{rate}</em>
        ) : null}
        <p>{hint}</p>
      </div>
      <div className={`dashboard-kpi-icon tone-${tone}`}>{icon}</div>
    </article>
  );
}

function DashboardView({
  agents,
  context,
  loading,
  onFiltersChange,
  onReportFilterReset,
  onSyncNow,
  reportData,
  reportDraftFilters,
  roleScope,
}) {
  const [dateModalOpen, setDateModalOpen] = React.useState(false);
  const [lastSynced, setLastSynced] = React.useState(() => new Date());

  React.useEffect(() => {
    if (reportData && !loading) {
      setLastSynced(new Date());
    }
  }, [reportData, loading]);

  const formattedLastSynced = 
    lastSynced.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
    ", " +
    lastSynced.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const funnelStages = reportData?.stages || [];
  const sourceBreakdown = reportData?.source_breakdown || [];
  const topSources = sourceBreakdown.slice(0, 4);
  // Dashboard always shows B2C only — b2c_leads === total_leads when campaign_type=b2c is applied
  const totalLeads = reportData?.summary?.b2c_leads ?? reportData?.summary?.total_leads ?? 0;
  const totalSourceLeads = Math.max(
    sourceBreakdown.reduce((sum, item) => sum + (item.total || 0), 0),
    1,
  );
  const agentBreakdown = reportData?.agent_breakdown || [];
  const topAgents = agentBreakdown.slice(0, 4);
  const maxAgentCalls = Math.max(...topAgents.map((item) => item.total_calls), 1);
  const canChooseAgent = roleScope?.scope !== "own_leads";
  const arc1 = Math.round(((topSources[0]?.total || 0) / totalSourceLeads) * 360);
  const arc2 = Math.round(
    (((topSources[0]?.total || 0) + (topSources[1]?.total || 0)) / totalSourceLeads) * 360,
  );
  const today = new Date().toISOString().slice(0, 10);
  const dashboardDateLabel = (() => {
    const { dateFrom, dateTo } = reportDraftFilters;
    if (!dateFrom && !dateTo) return "Filter by Date";
    if (dateFrom === dateTo && dateFrom === today) return "Today";
    if (dateFrom === dateTo) return formatShortDate(dateFrom);
    return `${formatShortDate(dateFrom) || "…"} – ${formatShortDate(dateTo) || "…"}`;
  })();

  return (
    <section className="analytics-shell">
      <section className="report-card dashboard-hero-card">
        <div className="dashboard-hero-copy">
          <h1>Sales Dashboard</h1>
          <p>Track and analyze your sales performance & lead pipeline.</p>
        </div>

        <div className="dashboard-filter-row">
          <div className="dashboard-filter-inputs">
            <DashboardFilterField icon={<CalendarIcon />} label="Date Range">
              <button
                type="button"
                className="dashboard-date-trigger"
                onClick={() => setDateModalOpen(true)}
              >
                <span>{dashboardDateLabel}</span>
              </button>
            </DashboardFilterField>

            {canChooseAgent ? (
              <DashboardFilterField icon={<UserGroupIcon />} label="Agent-Wise">
                <select
                  value={reportDraftFilters.assignedToId}
                  onChange={(e) => onFiltersChange({ assignedToId: e.target.value })}
                >
                  <option value="">All Agents</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                  ))}
                </select>
              </DashboardFilterField>
            ) : null}

            <DashboardFilterField icon={<GlobeIcon />} label="Campaign / Source-wise">
              <select
                value={reportDraftFilters.sourceId}
                onChange={(e) => onFiltersChange({ sourceId: e.target.value })}
              >
                <option value="">All Sources</option>
                {(context?.lead_sources || []).map((source) => (
                  <option key={source.id} value={source.id}>{source.name}</option>
                ))}
              </select>
            </DashboardFilterField>

            <DashboardFilterField icon={<PulseMiniIcon />} label="Lead Stage-Wise">
              <select
                value={reportDraftFilters.status}
                onChange={(e) => onFiltersChange({ status: e.target.value })}
              >
                <option value="">All Stages</option>
                {LEAD_STATUS_SEQUENCE.map((stage) => (
                  <option key={stage.value} value={stage.value}>{stage.label}</option>
                ))}
              </select>
            </DashboardFilterField>
          </div>

          <div className="dashboard-filter-actions">
            <button type="button" className="dashboard-reset-button" onClick={onReportFilterReset}>
              <ResetIcon />
              <span>Reset Filters</span>
            </button>
          </div>
        </div>
      </section>

      <section className="analytics-summary-grid dashboard-summary-grid dashboard-kpi-grid">
        <AnalyticsSummaryCard
          title="Total B2C Leads"
          value={formatCompactNumber(totalLeads)}
          hint={metricDelta(totalLeads)}
          tone="blue"
          icon={<UserGroupIcon />}
        />
        <AnalyticsSummaryCard
          title="Attempted"
          value={formatCompactNumber(reportData?.summary?.attempted || 0)}
          rate={`${formatRate(reportData?.summary?.attempted_rate || 0)}%`}
          hint={metricDelta(reportData?.summary?.attempted_rate || 0)}
          tone="sky"
          icon={<PhoneIcon />}
        />
        <AnalyticsSummaryCard
          title="Contacted"
          value={formatCompactNumber(reportData?.summary?.contacted || 0)}
          rate={`${formatRate(reportData?.summary?.contacted_rate || 0)}%`}
          hint={metricDelta(reportData?.summary?.contacted_rate || 0)}
          tone="violet"
          icon={<PhoneIcon />}
        />
        <AnalyticsSummaryCard
          title="Test Drive Sched."
          value={formatCompactNumber(reportData?.summary?.test_drive_scheduled || 0)}
          rate={`${formatRate(reportData?.summary?.test_drive_rate || 0)}%`}
          hint={metricDelta(reportData?.summary?.test_drive_rate || 0)}
          tone="amber"
          icon={<CalendarIcon />}
        />
        <AnalyticsSummaryCard
          title="Booked"
          value={formatCompactNumber(reportData?.summary?.booked || 0)}
          rate={`${formatRate(reportData?.summary?.conversion_rate || 0)}%`}
          hint={metricDelta(reportData?.summary?.conversion_rate || 0)}
          tone="green"
          icon={<BoltMiniIcon />}
        />
      </section>

      <section className="dashboard-grid">
        <article className="report-card dashboard-source-card">
          <div className="report-card-header">
            <div>
              <h2>Leads by Source</h2>
            </div>
          </div>

          <div className="dashboard-source-layout">
            <div
              className="dashboard-donut"
              style={{
                background: `conic-gradient(#3d6df2 0deg ${arc1}deg, #8d5cf6 ${arc1}deg ${arc2}deg, #4ec8d9 ${arc2}deg 360deg)`,
              }}
            >
              <div className="dashboard-donut-hole">
                <strong>{formatCompactNumber(totalLeads)}</strong>
                <span>B2C Leads</span>
              </div>
            </div>

            <div className="dashboard-source-table">
              <div className="dashboard-source-table-head">
                <span>Source</span>
                <span>Leads</span>
                <span>% of Total</span>
              </div>
              {topSources.map((item) => (
                <div className="dashboard-source-table-row" key={item.source_id}>
                  <span className="dashboard-source-name">
                    <i className={`dashboard-source-dot tone-${toneForSource(item.source_name, item.source_type)}`} />
                    {item.source_name}
                  </span>
                  <strong>{formatCompactNumber(item.total)}</strong>
                  <span>{formatRate((item.total / totalSourceLeads) * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        {roleScope?.show_agent_performance !== false && (
          <article className="report-card dashboard-agent-card">
            <div className="report-card-header">
              <div>
                <h2>Agent Performance</h2>
              </div>
            </div>

            <div className="dashboard-agent-table">
              <div className="dashboard-agent-table-head">
                <span>Agent</span>
                <span>Total Calls</span>
                <span>Performance</span>
              </div>

              {topAgents.map((item) => (
                <div className="dashboard-agent-row" key={item.id}>
                  <div className="dashboard-agent-primary">
                    <div className={`agent-avatar tone-${toneForAgent(item.badge)}`}>
                      {initialsForName(item.name)}
                    </div>
                    <div className="agent-performance-meta">
                      <strong>{item.name}</strong>
                      <span>{formatCompactNumber(item.total_calls)} calls</span>
                    </div>
                  </div>
                  <div className="dashboard-agent-tag">
                    <span className={`agent-badge ${toneForAgent(item.badge)}`}>{item.badge}</span>
                  </div>
                  <div className="dashboard-agent-progress">
                    <div className="agent-progress">
                      <span
                        style={{
                          width: `${Math.max((item.total_calls / maxAgentCalls) * 100, 18)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </article>
        )}
      </section>

      <section className="report-card dashboard-funnel-card">
        <div className="report-card-header">
          <div>
            <h2>Conversion Funnel Snapshot</h2>
          </div>
        </div>

        <div className="dashboard-funnel-grid dashboard-funnel-ribbon">
          {funnelStages.map((stage) => (
            <div key={stage.key} className={`dashboard-funnel-stage tone-${stage.tone}`}>
              <span>{stage.label.replace(" Leads", "")}</span>
              <strong>{formatCompactNumber(stage.value)}</strong>
              <small>{formatRate(stage.rate)}%</small>
            </div>
          ))}
        </div>

        <div className="dashboard-footer">
          <p>
            Note: Data shown is based on selected filters and date range.
          </p>
          <div className="dashboard-footer-sync">
            <span>Last synced: {formattedLastSynced}</span>
            <button
              type="button"
              className={`dashboard-sync-button${loading ? " is-syncing" : ""}`}
              onClick={onSyncNow || onReportFilterApply}
              disabled={loading}
              aria-busy={loading}
            >
              <SyncIcon spinning={loading} />
              <span>{loading ? "Syncing now..." : "Sync Now"}</span>
            </button>
          </div>
        </div>
      </section>

      <DateRangeModal
        open={dateModalOpen}
        startValue={reportDraftFilters.dateFrom}
        endValue={reportDraftFilters.dateTo}
        onApply={(start, end) => {
          onFiltersChange({ dateFrom: start, dateTo: end });
          setDateModalOpen(false);
        }}
        onClose={() => setDateModalOpen(false)}
      />
    </section>
  );
}

export default DashboardView;
